import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FlowrouteCard } from "@/components/FlowrouteCard";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ExternalLink, Plus, Edit, Trash2, Link as LinkIcon, Lock } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Integrations() {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    display_name: "",
    url: "",
    username: "",
    password: ""
  });

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setCurrentUser(JSON.parse(userData));
    }
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API}/integrations`);
      setIntegrations(response.data);
    } catch (error) {
      toast.error("Failed to fetch integrations");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingIntegration) {
        await axios.put(`${API}/integrations/${editingIntegration.id}`, formData);
        toast.success("Integration updated successfully");
      } else {
        await axios.post(`${API}/integrations`, formData);
        toast.success("Integration created successfully");
      }
      setOpen(false);
      resetForm();
      fetchIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save integration");
    }
  };

  const handleEdit = (integration) => {
    setEditingIntegration(integration);
    setFormData({
      display_name: integration.display_name,
      url: integration.url,
      username: integration.username || "",
      password: "" // Don't populate password for security
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this integration?")) return;
    try {
      await axios.delete(`${API}/integrations/${id}`);
      toast.success("Integration deleted successfully");
      fetchIntegrations();
    } catch (error) {
      toast.error("Failed to delete integration");
    }
  };

  const resetForm = () => {
    setFormData({
      display_name: "",
      url: "",
      username: "",
      password: ""
    });
    setEditingIntegration(null);
  };

  const openIntegration = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const isAdmin = currentUser?.role === 'admin';

  return (
    <div data-testid="integrations-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Integrations</h1>
          <p className="text-slate-600">Quick access to external tools and platforms</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={(isOpen) => {
            setOpen(isOpen);
            if (!isOpen) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg">
                <Plus className="w-4 h-4 mr-2" />
                Add Integration
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>{editingIntegration ? "Edit Integration" : "Add New Integration"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="display_name">Display Name *</Label>
                  <Input
                    id="display_name"
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    required
                    className="mt-1"
                    placeholder="e.g., QuickBooks, Slack"
                  />
                </div>
                <div>
                  <Label htmlFor="url">URL *</Label>
                  <Input
                    id="url"
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    required
                    className="mt-1"
                    placeholder="https://example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="username">Username (Optional)</Label>
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="mt-1"
                    placeholder="Username for quick reference"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password (Optional)</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="mt-1"
                    placeholder="Stored securely"
                  />
                  <p className="text-xs text-slate-500 mt-1">Leave blank to keep existing password</p>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                    {editingIntegration ? "Update" : "Create"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <FlowrouteCard isAdmin={isAdmin} />

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : integrations.length === 0 ? (
        <Card className="bg-white border-0 shadow-lg">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <LinkIcon className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No integrations yet</h3>
            <p className="text-slate-500">{isAdmin ? "Add your first integration to get started" : "No integrations have been set up"}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {integrations.map((integration) => (
            <Card key={integration.id} className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer" onClick={() => openIntegration(integration.url)}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <LinkIcon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{integration.display_name}</CardTitle>
                      {isAdmin && (
                        <div className="flex gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(integration)}
                            className="h-7 text-xs"
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(integration.id)}
                            className="h-7 text-xs text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-slate-400" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <LinkIcon className="w-3 h-3" />
                    <span className="truncate">{integration.url}</span>
                  </div>
                  {integration.username && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="font-semibold">Username:</span>
                      <span>{integration.username}</span>
                    </div>
                  )}
                  {integration.encrypted_password && integration.encrypted_password !== '***' && isAdmin && (
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <Lock className="w-3 h-3" />
                      <span>Password saved</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!isAdmin && integrations.length > 0 && (
        <div className="mt-6">
          <Badge variant="outline" className="text-slate-500">
            Read-only view - Contact admin to manage integrations
          </Badge>
        </div>
      )}
    </div>
  );
}
